<script setup lang="ts">
import { watch } from "vue";
import { RouterView, useRoute, useRouter } from "vue-router";

import AppHeader from "@/components/app/AppHeader.vue";
import OfflineBanner from "@/components/app/OfflineBanner.vue";
import ToastHost from "@/components/app/ToastHost.vue";
import UpdatePrompt from "@/components/app/UpdatePrompt.vue";
import { useAuthStore } from "@/stores/auth";
import { useConnectionsStore } from "@/stores/connections";
import { useLabStore } from "@/stores/lab";
import { useProductsStore } from "@/stores/products";
import { usePromptsStore } from "@/stores/prompts";

const auth = useAuthStore();
const products = useProductsStore();
const connections = useConnectionsStore();
const prompts = usePromptsStore();
const lab = useLabStore();
const route = useRoute();
const router = useRouter();

// Sesion cerrada o expirada (401, refresh fallido, otra pestaña): vuelta al login conservando la ruta.
watch(
  () => auth.isAuthenticated,
  (authed) => {
    if (authed) return;
    products.reset();
    connections.reset();
    prompts.reset();
    lab.reset();
    if (!route.meta.public) {
      void router.replace({ name: "login", query: route.fullPath !== "/" ? { redirect: route.fullPath } : {} });
    }
  },
);
</script>

<template>
  <a class="skip-link" href="#main">Saltar al contenido</a>
  <AppHeader v-if="auth.isAuthenticated && !route.meta.public" />
  <OfflineBanner />
  <main id="main" tabindex="-1">
    <RouterView v-slot="{ Component }">
      <component :is="Component" :key="(route.name as string) + (route.params.id ?? '')" />
    </RouterView>
  </main>
  <ToastHost />
  <UpdatePrompt />
</template>

<style scoped>
main {
  outline: none;
}
.skip-link {
  position: absolute;
  left: var(--space-2);
  top: -100px;
  z-index: 50;
  padding: var(--space-2) var(--space-3);
  background: var(--color-text);
  color: var(--color-bg);
  border-radius: var(--radius-md);
}
.skip-link:focus {
  top: calc(var(--space-2) + var(--safe-top));
}
</style>
